import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import {
  fetchCsvImportLogByReferenceAction,
  updateCsvImportLogConfirmationAction
} from '../../redux/actions/csvImportLogs/csvImportLogsAction';
import { fetchImportedInventory } from '../../redux/actions/importedInventory/importedInventoryAction';
import { saveAs } from 'file-saver';

const ImportBatchDetailPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { referenceNumber } = useParams();
  const user = useSelector((state) => state.login.user); // Add this line
  
  const {
    logByReference,
    logByReferenceLoading,
    logByReferenceError
  } = useSelector((state) => state.csvImportLogs);
  
  const {
    importedInventory,
    loading: inventoryLoading
  } = useSelector((state) => state.importedInventory);

  const [filteredInventory, setFilteredInventory] = useState([]);

  const loadBatchDetails = useCallback(async () => {
    try {
      await Promise.all([
        dispatch(fetchCsvImportLogByReferenceAction(referenceNumber)),
        dispatch(fetchImportedInventory())
      ]);
    } catch (error) {
      console.error('Error loading batch details:', error);
    }
  }, [dispatch, referenceNumber]);

  useEffect(() => {
    if (referenceNumber) {
      loadBatchDetails();
    }
  }, [loadBatchDetails, referenceNumber]);

  useEffect(() => {
    if (logByReference && importedInventory.length > 0) {
      // Filter inventory by the batch reference number
      const filtered = importedInventory.filter(item => 
        item.reference_number === referenceNumber || 
        item.batch_number === referenceNumber
      );
      setFilteredInventory(filtered);
    }
  }, [logByReference, importedInventory, referenceNumber]);

  const handleBack = () => {
    navigate('/csv-import-logs');
  };

  const handleRefresh = () => {
    loadBatchDetails();
    toast.success('Data refreshed successfully');
  };

  const handleConfirm = async () => {
    if (!logByReference) return;
    try {
      await dispatch(updateCsvImportLogConfirmationAction(logByReference.id, !logByReference.is_confirm));
      toast.success(`Import batch ${logByReference.is_confirm ? 'unconfirmed' : 'confirmed'} successfully`);
      loadBatchDetails(); // Refresh data
    } catch (error) {
      const errorMessage = (error && error.message) ? error.message : 'Failed to update confirmation status';
      toast.error(errorMessage);
    }
  };

  const handleDownloadNotSaved = async () => {
    if (!logByReference?.reference_number) return;
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/api/imported-inventory/not-saved/${logByReference.reference_number}/csv`);
      if (!response.ok) throw new Error('No not saved records found');
      const blob = await response.blob();
      saveAs(blob, `not_saved_records_${logByReference.reference_number}.csv`);
    } catch (err) {
      alert('No not saved records found or error downloading.');
    }
  };

  const inventoryColumns = [
    { name: 'Reference Number', selector: row => row.reference_number, sortable: true },
    { name: 'Batch Number', selector: row => row.batch_number, sortable: true },
    { name: 'Product', selector: row => row.product, sortable: true },
    { name: 'Quantity', selector: row => row.qty, sortable: true },
    { name: 'BOM Dia Qty', selector: row => row.bom_dia_qty, sortable: true },
    { name: 'BOM CLR Qty', selector: row => row.bom_clr_qty, sortable: true },
    { name: 'Issue Total Qty', selector: row => row.issue_tot_qty, sortable: true },
    { name: 'Stone Weight', selector: row => row.stone_weight, sortable: true },
    { name: 'Gross Weight', selector: row => row.gross_weight, sortable: true },
    { name: 'Net Weight', selector: row => row.net_weight, sortable: true },
    { name: 'Net Weight Purity', selector: row => row.net_weight_purity, sortable: true },
    { name: 'Warehouse', selector: row => row.warehouse, sortable: true },
    { name: 'Vendor Site', selector: row => row.vendor_site, sortable: true },
    { name: 'JO Number', selector: row => row.jo_number, sortable: true },
    { name: 'JO Date', selector: row => row.jo_date ? new Date(row.jo_date).toLocaleDateString() : '', sortable: true },
    { name: 'Production Route', selector: row => row.production_route, sortable: true },
    { name: 'HM Product Category', selector: row => row.hm_product_category, sortable: true },
    { name: 'HM Purity', selector: row => row.hm_purity, sortable: true },
    { 
      name: 'Batch Status', 
      selector: row => row.batch_no_status, 
      sortable: true,
      cell: row => (
        <span style={{
          display: 'inline-block',
          border: row.batch_no_status === 0 ? '1px solid #ed6c02' : 
                 row.batch_no_status === 1 ? '1px solid #1976d2' : 
                 row.batch_no_status === 2 ? '1px solid #2e7d32' : '1px solid #757575',
          color: row.batch_no_status === 0 ? '#ed6c02' : 
                 row.batch_no_status === 1 ? '#1976d2' : 
                 row.batch_no_status === 2 ? '#2e7d32' : '#757575',
          borderRadius: '16px',
          padding: '2px 12px',
          fontSize: '0.875rem',
          fontWeight: 500,
          background: row.batch_no_status === 0 ? '#fff3e0' : 
                     row.batch_no_status === 1 ? '#e3f2fd' : 
                     row.batch_no_status === 2 ? '#e8f5e9' : '#f5f5f5',
          minWidth: 32,
          textAlign: 'center',
        }}>
          {row.batch_no_status === 0 ? 'Pending' : 
           row.batch_no_status === 1 ? 'In Progress' : 
           row.batch_no_status === 2 ? 'Completed' : 'Unknown'}
        </span>
      ),
      ignoreRowClick: true
    },
    { name: 'Created By', selector: row => row.created_by || row.createdby, sortable: true },
    { name: 'Created At', selector: row => row.created_at ? new Date(row.created_at).toLocaleString() : '', sortable: true },
    { name: 'Updated By', selector: row => row.updated_by, sortable: true },
    { name: 'Updated At', selector: row => row.updated_at ? new Date(row.updated_at).toLocaleString() : '', sortable: true },
  ];

  if (logByReferenceLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (logByReferenceError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {logByReferenceError}
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          <ArrowBackIcon /> Back to Logs
        </Button>
      </Box>
    );
  }

  if (!logByReference) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Import batch not found
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          <ArrowBackIcon /> Back to Logs
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Typography variant="h4">
            Import Batch: {logByReference.reference_number}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={logByReferenceLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Batch Details */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Batch Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Reference Number:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {logByReference.reference_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Import Date:
                  </Typography>
                  <Typography variant="body1">
                    {new Date(logByReference.imported_date_time).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Import Time:
                  </Typography>
                  <Typography variant="body1">
                    {new Date(logByReference.imported_date_time).toLocaleTimeString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status:
                  </Typography>
                  <span style={{
                    display: 'inline-block',
                    border: logByReference.is_confirm ? '1px solid #2e7d32' : '1px solid #ed6c02',
                    color: logByReference.is_confirm ? '#2e7d32' : '#ed6c02',
                    borderRadius: '16px',
                    padding: '2px 12px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    background: logByReference.is_confirm ? '#e8f5e9' : '#fff3e0',
                    minWidth: 32,
                    textAlign: 'center',
                  }}>{logByReference.is_confirm ? 'Confirmed' : 'Pending'}</span>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Import Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Total Records:
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {logByReference.total_records}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Saved:
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {logByReference.saved_records}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Not Saved:
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {logByReference.not_saved_records}
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant={logByReference.is_confirm ? "outlined" : "contained"}
                  color={logByReference.is_confirm ? "warning" : "success"}
                  startIcon={logByReference.is_confirm ? <CancelIcon /> : <CheckCircleIcon />}
                  onClick={handleConfirm}
                  fullWidth
                >
                  {logByReference.is_confirm ? 'Unconfirm Batch' : 'Confirm Batch'}
                </Button>
              </Box>
              <Button variant="outlined" color="error" onClick={handleDownloadNotSaved} fullWidth sx={{ mt: 1 }}>
                Download Not Saved Records CSV
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Imported Inventory Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Imported Inventory Records
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Showing {filteredInventory.length} records for batch {logByReference.reference_number}
        </Typography>
        
        <DataTable
          columns={inventoryColumns}
          data={filteredInventory}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 50]}
          highlightOnHover
          pointerOnHover
          progressPending={inventoryLoading}
          progressComponent={<CircularProgress />}
          dense
          responsive
          noDataComponent={
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <p>No imported inventory records found for this batch</p>
            </Box>
          }
        />
      </Paper>
    </Box>
  );
};

export default ImportBatchDetailPage; 